import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';
import Toast from '../components/Toast';

/**
 * AdminCategories — /admin/categories
 * Add / rename / recolor / activate / deactivate / delete profile categories.
 * Deactivating is soft — existing users keep the tag; new users can't pick it.
 * Deleting is only allowed if `users_count` is 0.
 */
const COLOR_OPTIONS = [
  { key: 'c-orange', label: 'Orange', swatch: '#FF5722' },
  { key: 'c-pink',   label: 'Pink',   swatch: '#E040FB' },
  { key: 'c-purple', label: 'Purple', swatch: '#00B4FF' },
  { key: 'c-blue',   label: 'Blue',   swatch: '#00B4FF' },
  { key: 'c-green',  label: 'Green',  swatch: '#B4FF00' },
];

export default function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('c-purple');
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // {id, label, color}

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/categories');
      setRows(data.categories || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load categories');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createCat = async () => {
    const label = newLabel.trim();
    if (!label) { setToast({ msg: 'Enter a category name.' }); return; }
    setBusyId('__new__');
    try {
      await api.post('/admin/categories', { label, color: newColor });
      setNewLabel(''); setNewColor('c-purple');
      setToast({ msg: `Added "${label}" ✓` });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Create failed' });
    } finally { setBusyId(null); }
  };

  const patch = async (row, updates, msg) => {
    setBusyId(row.id);
    try {
      await api.patch(`/admin/categories/${row.id}`, updates);
      setToast({ msg });
      setEditing(null);
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Update failed' });
    } finally { setBusyId(null); }
  };

  const removeCat = async (row) => {
    if (!window.confirm(`Delete category "${row.label}"? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/admin/categories/${row.id}`);
      setToast({ msg: `Deleted "${row.label}"` });
      refresh();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Delete failed' });
    } finally { setBusyId(null); }
  };

  return (
    <AdminLayout active="categories">
      <div className="admin-page" data-testid="admin-categories-page">
        <header className="admin-page-header">
          <h1 className="admin-page-title">Categories</h1>
          <p className="admin-page-sub">
            Tags creators pick during onboarding + filter chips on Home. Deactivating a tag
            keeps existing users; new users just can't pick it. Delete only works when no one uses it.
          </p>
        </header>

        {/* Add new */}
        <section className="admin-section">
          <h2 className="admin-section-title">Add a category</h2>
          <div className="settings-card">
            <div className="cat-add-row">
              <input
                type="text"
                className="admin-search"
                style={{ maxWidth: 260 }}
                placeholder="e.g. Podcaster"
                value={newLabel}
                maxLength={40}
                onChange={(e) => setNewLabel(e.target.value)}
                data-testid="cat-new-label"
              />
              <div className="cat-color-picker">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`cat-swatch${newColor === c.key ? ' active' : ''}`}
                    style={{ background: c.swatch }}
                    onClick={() => setNewColor(c.key)}
                    title={c.label}
                    data-testid={`cat-new-color-${c.key}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className="admin-btn-primary"
                onClick={createCat}
                disabled={busyId === '__new__'}
                data-testid="cat-add-btn"
              >
                {busyId === '__new__' ? '…' : '+ Add'}
              </button>
            </div>
          </div>
        </section>

        {loading && <div className="admin-loading">Loading…</div>}
        {err && <div className="admin-error">{err}</div>}

        {!loading && !err && (
          <section className="admin-section">
            <h2 className="admin-section-title">All categories</h2>
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="cat-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Label</th>
                    <th style={{ width: 90 }}>Color</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Users</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 220 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isEditing = editing?.id === r.id;
                    return (
                      <tr key={r.id} data-testid={`cat-row-${r.id}`} className={r.active ? '' : 'admin-muted-row'}>
                        <td className="admin-table-num">{r.order + 1}</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              className="admin-search"
                              value={editing.label}
                              maxLength={40}
                              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                              data-testid={`cat-edit-label-${r.id}`}
                            />
                          ) : (
                            <b>{r.label}</b>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="cat-color-picker">
                              {COLOR_OPTIONS.map((c) => (
                                <button
                                  key={c.key}
                                  type="button"
                                  className={`cat-swatch${editing.color === c.key ? ' active' : ''}`}
                                  style={{ background: c.swatch }}
                                  onClick={() => setEditing({ ...editing, color: c.key })}
                                  data-testid={`cat-edit-color-${r.id}-${c.key}`}
                                />
                              ))}
                            </div>
                          ) : (
                            <span
                              className="cat-swatch small"
                              style={{ background: (COLOR_OPTIONS.find(c => c.key === r.color) || COLOR_OPTIONS[2]).swatch }}
                            />
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }} className="admin-mono">{r.users_count.toLocaleString()}</td>
                        <td>
                          {r.active
                            ? <span className="admin-tag tag-ok">Active</span>
                            : <span className="admin-tag">Hidden</span>}
                        </td>
                        <td>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                type="button"
                                className="admin-btn-sm ok"
                                disabled={busyId === r.id}
                                onClick={() => patch(r, { label: editing.label.trim(), color: editing.color }, 'Renamed ✓')}
                                data-testid={`cat-save-${r.id}`}
                              >
                                Save
                              </button>
                              <button type="button" className="admin-btn-sm" onClick={() => setEditing(null)}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                type="button"
                                className="admin-btn-sm"
                                onClick={() => setEditing({ id: r.id, label: r.label, color: r.color })}
                                data-testid={`cat-edit-${r.id}`}
                              >
                                Edit
                              </button>
                              {r.active ? (
                                <button
                                  type="button"
                                  className="admin-btn-sm warn"
                                  disabled={busyId === r.id}
                                  onClick={() => patch(r, { active: false }, `Hid "${r.label}"`)}
                                  data-testid={`cat-hide-${r.id}`}
                                >
                                  Hide
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="admin-btn-sm ok"
                                  disabled={busyId === r.id}
                                  onClick={() => patch(r, { active: true }, `Restored "${r.label}"`)}
                                  data-testid={`cat-show-${r.id}`}
                                >
                                  Show
                                </button>
                              )}
                              {r.users_count === 0 && (
                                <button
                                  type="button"
                                  className="admin-btn-sm warn"
                                  disabled={busyId === r.id}
                                  onClick={() => removeCat(r)}
                                  data-testid={`cat-delete-${r.id}`}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AdminLayout>
  );
}
